/*
This file is located in the main branch
Last time the file was updated (UTC): [02.07.2024 18:53:04]
Permissions to update outside the main branch: [false]
*/

import { Button, DataGridTooltipedFormatter, Input } from '@finbeat/react-toolkit';
import { BODatePicker } from 'base-components/BODatePicker/BODatePicker';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useUserActionLogWorkplaceState } from './UserActionLog';

type UserActionLogDetailsModalProps = {
    isOpen: boolean;
    toggle: () => void;
};

const JSON_INDENTATION = 2;

const indentJson = (jsonStr: string | undefined): string | undefined => {
    if (jsonStr != null && jsonStr.trim() !== '' && !jsonStr.endsWith('...')) {
        try {
            return JSON.stringify(JSON.parse(jsonStr), undefined, JSON_INDENTATION);
        } catch (error) {
            console.error(error);
        }
    }
    return jsonStr;
};

const getRowsCount = (content: string | undefined): number => {
    return (content?.length || 0) > 10 ? 10 : 2;
};

const UserActionLogDetailsModal = (props: UserActionLogDetailsModalProps) => {
    const { isOpen = false, toggle } = props;

    const { t } = useTranslation();
    const { currentItem } = useUserActionLogWorkplaceState();
    const [mutableUserActionLogItem, changeUserActionLogItem] = useState(currentItem);

    const closeModal = () => {
        !mutableUserActionLogItem?.id && changeUserActionLogItem(null);
        toggle();
    };

    useEffect(() => changeUserActionLogItem(currentItem), [currentItem]);

    const {
        methodControllerAction,
        type,
        username,
        sd,
        ed,
        method,
        controller,
        action,
        actionParams,
        originalUrl,
        userHost,
        userAgent,
        form,
        queryString,
        referer,
        headers,
        cookies,
        statusCode,
        responseHeaders,
        response,
    } = mutableUserActionLogItem || {};

    return (
        <Modal
            size="lg"
            isOpen={isOpen}
            toggle={() => closeModal()}
            className="user-action-log__modal"
            scrollable
        >
            <ModalHeader toggle={() => closeModal()}>
                <DataGridTooltipedFormatter tooltipText={methodControllerAction}>
                    {`#${methodControllerAction}`}
                </DataGridTooltipedFormatter>
            </ModalHeader>

            <ModalBody className="custom-scrollbar">
                <div className="row">
                    <div className="col">
                        <Input
                            id="info-type-field"
                            value={type}
                            name="type"
                            readOnly={true}
                            label={t('BackOfficeType')}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col">
                        <Input
                            id="info-username-field"
                            value={username}
                            name="username"
                            readOnly={true}
                            label={t('BackOfficeUserName')}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col">
                        <BODatePicker
                            id="info-start-date-field"
                            value={sd}
                            disabled={true}
                            inputProps={{ label: t('BackOfficeStartDate') }}
                        />
                    </div>
                    <div className="col">
                        <BODatePicker
                            id="info-endDate-field"
                            value={ed}
                            disabled={true}
                            inputProps={{ label: t('BackOfficeEndDate') }}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col">
                        <Input
                            id="info-method-field"
                            value={method}
                            name="method"
                            readOnly={true}
                            label={t('BackOfficeMethod')}
                        />
                    </div>
                    <div className="col">
                        <Input
                            id="info-controller-field"
                            value={controller}
                            name="controller"
                            readOnly={true}
                            label={t('BackOfficeController')}
                        />
                    </div>
                    <div className="col">
                        <Input
                            id="info-action-field"
                            value={action}
                            name="action"
                            readOnly={true}
                            label={t('BackOfficeAction')}
                        />
                    </div>
                </div>
                <Input
                    type="textarea"
                    inputClassName="textarea-height-auto"
                    rows={getRowsCount(actionParams)}
                    id="info-action-params-field"
                    value={indentJson(actionParams)}
                    name="actionParams"
                    readOnly={true}
                    label={t('BackOfficeActionParams')}
                />
                <Input
                    id="info-original-url-field"
                    value={originalUrl}
                    name="originalUrl"
                    readOnly={true}
                    label={t('BackOfficeOriginalUrl')}
                />
                <Input
                    id="info-user-host-field"
                    value={userHost}
                    name="userHost"
                    readOnly={true}
                    label={t('BackOfficeUserHost')}
                />
                <Input
                    id="info-user-agent-field"
                    value={userAgent}
                    name="userAgent"
                    readOnly={true}
                    label={t('BackOfficeUserAgent')}
                />
                <Input
                    id="info-form-field"
                    value={form}
                    name="form"
                    readOnly={true}
                    label={t('BackOfficeFormData')}
                />
                <Input
                    type="textarea"
                    inputClassName="textarea-height-auto"
                    rows={getRowsCount(queryString)}
                    id="info-query-string-field"
                    value={indentJson(queryString)}
                    name="queryString"
                    readOnly={true}
                    label={t('BackOfficeQueryString')}
                />
                <Input
                    id="info-referer-field"
                    value={referer}
                    name="referer"
                    readOnly={true}
                    label={t('BackOfficeHttpReferer')}
                />
                <Input
                    type="textarea"
                    inputClassName="textarea-height-auto"
                    rows={getRowsCount(headers)}
                    id="info-headers-field"
                    value={indentJson(headers)}
                    name="headers"
                    readOnly={true}
                    label={t('BackOfficeHttpRequestHeaders')}
                />
                <Input
                    type="textarea"
                    id="info-cookies-field"
                    inputClassName="textarea-height-auto"
                    rows={getRowsCount(cookies)}
                    value={indentJson(cookies)}
                    name="cookies"
                    readOnly={true}
                    label={t('BackOfficeHttpCookies')}
                />
                <Input
                    id="info-status-code-field"
                    value={`${statusCode}`}
                    name="statusCode"
                    readOnly={true}
                    label={t('BackOfficeStatusCode')}
                />
                <Input
                    type="textarea"
                    id="info-response-headers-field"
                    value={indentJson(responseHeaders)}
                    inputClassName="textarea-height-auto"
                    rows={getRowsCount(responseHeaders)}
                    name="responseHeaders"
                    readOnly={true}
                    label={t('BackOfficeHttpResponseHeaders')}
                />
                <Input
                    id="info-response-field"
                    type="textarea"
                    value={indentJson(response)}
                    inputClassName="textarea-height-auto"
                    rows={getRowsCount(response)}
                    name="response"
                    readOnly={true}
                    label={t('BackOfficeResponse')}
                />
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={() => closeModal()}>
                    <Trans i18nKey="BackOfficeCancel" />
                </Button>
                <Button color="success" onClick={() => closeModal()}>
                    <Trans i18nKey="BackOfficeClose" />
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default UserActionLogDetailsModal;
